import type { JSX } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { KB_PATH, kbArticlePath, kbFolderPath } from "./kb-paths.js";

/**
 * Where an old Documentation URL lands. Pure, so the whole table is one unit
 * test. Two generations of old URL exist:
 *
 * The `/knowledge-base/*` fortnight (2026-09-14 → 2026-09-17), when the
 * section was called "Knowledge Base". Same shapes, other prefix — the rest
 * of the path, the query and the hash are carried over verbatim:
 *
 *   /knowledge-base                         → /documentation
 *   /knowledge-base?q=…                     → /documentation?q=…
 *   /knowledge-base/category/:slug          → /documentation/category/:slug
 *   /knowledge-base/:slug                   → /documentation/:slug
 *
 * The pre-2026-09-14 tree-and-shelves shapes:
 *
 *   /documentation/search?q=…               → /documentation?q=…  (the
 *                                             landing page IS the search page)
 *   /documentation/type/:slug               → /documentation/category/:slug
 *   /documentation/library/:slug            → /documentation/:slug
 *   /documentation/release/:id              → /releases
 *   /documentation/type/:slug/release/:id   → /releases
 *   /documentation/type/:slug/release/unversioned → the folder (that segment
 *                                             always meant "the library shelf")
 *
 * The two release-shaped URLs go to Releases because release documentation
 * is read under its version there and nowhere else.
 */
export function legacyDocumentationTarget(pathname: string, search = "", hash = ""): string {
  const kb = /^\/knowledge-base(?=\/|$)/;
  if (kb.test(pathname)) {
    const rest = pathname.replace(kb, "").replace(/\/+$/, "");
    return `${KB_PATH}${rest}${search}${hash}`;
  }

  const segs = pathname
    .replace(/^\/documentation\/?/, "")
    .split("/")
    .filter(Boolean)
    .map((s) => decodeURIComponent(s));

  const [head, slug, third, fourth] = segs;
  if (!head) return KB_PATH;
  if (head === "search") return `${KB_PATH}${search}`;
  if (head === "library" && slug) return kbArticlePath(slug);
  if (head === "release") return "/releases";
  if (head === "type" && slug) {
    if (third === "release" && fourth && fourth !== "unversioned") return "/releases";
    return kbFolderPath(slug);
  }
  return KB_PATH;
}

/** Mounted on the old URL shapes; replaces the history entry so Back does not
 * bounce through the redirect. */
export function LegacyDocumentationRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={legacyDocumentationTarget(pathname, search, hash)} replace />;
}
